import { useEffect, useState } from 'react';
import { getDocumentationAttachmentDownloadUrl } from '../../services/documentationApi';
import { parseDocsAttachmentId } from '../../utils/documentationInlineAttachments';

type DocumentationMarkdownImageProps = {
  src?: string;
  alt?: string;
  pageId?: string | null;
};

/**
 * Resolve docs-attachment:{id} para URL pré-assinada; mantém data:/http(s) direto.
 */
export function DocumentationMarkdownImage({ src, alt, pageId }: DocumentationMarkdownImageProps) {
  const attachmentId = parseDocsAttachmentId(src);
  const [resolvedSrc, setResolvedSrc] = useState<string | undefined>(() =>
    attachmentId ? undefined : src,
  );
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (!attachmentId) {
      setResolvedSrc(src);
      setFailed(false);
      return;
    }

    if (!pageId) {
      setResolvedSrc(undefined);
      setFailed(true);
      return;
    }

    setResolvedSrc(undefined);
    setFailed(false);

    void (async () => {
      try {
        const download = await getDocumentationAttachmentDownloadUrl(pageId, attachmentId);
        if (!cancelled) {
          setResolvedSrc(download.url);
        }
      } catch {
        if (!cancelled) {
          setFailed(true);
          setResolvedSrc(undefined);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [attachmentId, pageId, src]);

  if (failed || (!attachmentId && !src?.trim())) {
    return (
      <span className="docs-md-prose__img-fallback" role="img" aria-label={alt || 'Imagem'}>
        {alt || 'Imagem indisponível'}
      </span>
    );
  }

  if (!resolvedSrc) {
    return <span className="docs-md-prose__img-loading" aria-hidden />;
  }

  return <img src={resolvedSrc} alt={alt ?? ''} className="docs-md-prose__img" loading="lazy" />;
}
